import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Industries from "@/pages/Industries";
import About from "@/pages/About";
import FreeAudit from "@/pages/FreeAudit";
import Resources from "@/pages/Resources";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import NotFound from "@/pages/NotFound";

const ACCESSIBILITY_STATEMENT = {
    title: "Accessibility Statement",
    slug: "accessibility-statement",
    lead: "AccessNova is committed to digital accessibility for every visitor. This statement outlines our conformance, the tooling we use, and how to reach us with feedback.",
    sections: [
        {
            heading: "Our commitment",
            paragraphs: [
                "AccessNova aims to conform with WCAG 2.2 Level AA across every page of accessnova.co. Accessibility is not a deliverable — it's an ongoing practice — and the work is never complete.",
            ],
        },
        {
            heading: "Conformance status",
            paragraphs: [
                "This site is designed and tested for WCAG 2.2 Level AA conformance. We perform manual reviews using NVDA, JAWS, VoiceOver, and TalkBack, plus automated scans with axe DevTools, WAVE, and Lighthouse.",
            ],
            list: [
                "Color contrast meets 4.5:1 for body text and 3:1 for non-text UI",
                "Keyboard navigation across the site, with visible focus indicators",
                "Semantic landmarks and skip-to-content link",
                "Respects prefers-reduced-motion",
                "Touch targets ≥ 44×44 px on mobile",
            ],
        },
        {
            heading: "Known limitations",
            paragraphs: [
                "We are not aware of any blocking accessibility barriers at the time of last review. Edge cases occasionally surface — please report them so we can fix quickly.",
            ],
        },
        {
            heading: "Feedback",
            paragraphs: [
                "If you encounter an accessibility barrier, please email hello@accessnova.co with the page URL and a short description. We aim to respond within one business day.",
            ],
        },
    ],
};

const PRIVACY = {
    title: "Privacy Policy",
    slug: "privacy",
    lead: "We collect only what we need to respond to you, and we never sell your data. This is a brief Phase 1 policy — a comprehensive GDPR/DPDP-compliant version is in progress.",
    sections: [
        {
            heading: "What we collect",
            paragraphs: [
                "Information you submit through forms (name, email, company, message). Basic technical telemetry to keep the site running (request logs, anonymized analytics).",
            ],
        },
        {
            heading: "How we use it",
            paragraphs: [
                "To respond to your enquiry, deliver requested audits, and improve our services. We do not share or sell personal data to third parties.",
            ],
        },
        {
            heading: "Your rights",
            paragraphs: [
                "You can request access, correction, or deletion of your data at any time by emailing hello@accessnova.co. Under GDPR/DPDP, you also have the right to portability and objection.",
            ],
        },
        {
            heading: "Cookies",
            paragraphs: [
                "We use a small number of essential cookies and (with your consent) analytics cookies. You can withdraw consent at any time via the cookie banner or your browser settings.",
            ],
        },
    ],
};

const TERMS = {
    title: "Terms of Service",
    slug: "terms",
    lead: "By using accessnova.co you agree to these brief terms. A more detailed agreement applies once a project engagement is signed.",
    sections: [
        {
            heading: "Service",
            paragraphs: [
                "Content on this site is provided for informational purposes. Audits, reports, and other deliverables are governed by signed Statements of Work.",
            ],
        },
        {
            heading: "Liability",
            paragraphs: [
                "To the extent permitted by law, AccessNova is not liable for indirect or consequential damages arising from website content. Engagements include their own liability terms.",
            ],
        },
        {
            heading: "Governing law",
            paragraphs: [
                "These terms are governed by the laws of India. Disputes will be resolved in the courts of competent jurisdiction in India unless agreed otherwise.",
            ],
        },
    ],
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ScrollToTop />
                <Layout>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/services" element={<Services />} />
                        <Route path="/industries" element={<Industries />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/free-audit" element={<FreeAudit />} />
                        <Route path="/resources" element={<Resources />} />
                        <Route path="/resources/:slug" element={<BlogPost />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/accessibility-statement" element={<LegalPage {...ACCESSIBILITY_STATEMENT} />} />
                        <Route path="/accessibility" element={<LegalPage {...ACCESSIBILITY_STATEMENT} />} />
                        <Route path="/privacy" element={<LegalPage {...PRIVACY} />} />
                        <Route path="/terms" element={<LegalPage {...TERMS} />} />
                        <Route
                            path="/sitemap"
                            element={<PlaceholderPage slug="sitemap" title="Sitemap"
                                description="An HTML sitemap will be added shortly." />}
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Layout>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </div>
    );
}

export default App;
